import React, { useContext } from 'react';
import { LangContext } from '../../contexts/langContext';
import { VARNA } from '../../static/jyotish_varna';
import { DiamondRadarChart } from '../UiKit/DiamondRadarChart';
import { TooltipH } from '../UiKit/TooltipH';
import '../css/jyotish-chart.css';

/** Clockwise from top: brahmin → kshatriya → shudra → vaishya (left). */
const AXES = [
  { id: 'brahmin', angle: 0 },
  { id: 'kshatriya', angle: 90 },
  { id: 'shudra', angle: 180 },
  { id: 'vaishya', angle: 270 },
];

/** Role → segment color on the data shape. */
const ROLE_COLORS = {
  core: '#1c1c1c',
  inner: '#7a1520',
  manifestation: '#e6c43a',
  none: '#d4d0c8',
};

const INDICATOR_LABEL_KEYS = {
  moon_D1: 'planet_Moon',
  moon_D9: 'jyotish_moon_d9',
  ascendant_D1: 'jyotish_lagna_label',
  sun_D1: 'planet_Sun',
};

const rolesForVarna = (varnaId, levels, $ui) => {
  if (!levels || !varnaId) return [];
  const roles = [];
  if (levels.core === varnaId) roles.push($ui('jyotish_varna_primary'));
  if (levels.inner === varnaId) roles.push($ui('jyotish_varna_inner'));
  if ((levels.manifestation || []).some((m) => m.varna === varnaId)) {
    roles.push($ui('jyotish_varna_manifestation'));
  }
  return roles;
};

/** Prefer core → inner → manifestation for an axis segment. */
const roleKeyForVarna = (varnaId, levels, count) => {
  if (!count || !varnaId || !levels) return 'none';
  if (levels.core === varnaId) return 'core';
  if (levels.inner === varnaId) return 'inner';
  if ((levels.manifestation || []).some((m) => m.varna === varnaId)) return 'manifestation';
  return 'none';
};

/**
 * Varna Profile radar — thin wrapper over DiamondRadarChart with role-based conic colors.
 * Top = brahmin, right = kshatriya, bottom = shudra, left = vaishya (masked themes).
 */
export function VarnaRadarChart({ distribution, profile }) {
  const { $ui, $jyotish, language } = useContext(LangContext);

  if (!distribution) return null;

  const theme = (id) => VARNA[id].theme[language] || VARNA[id].theme.en;
  const emoji = (id) => $jyotish(VARNA[id].uiKey, 'emoji');

  const values = Object.fromEntries(AXES.map(({ id }) => [id, distribution[id] || 0]));
  const colors = AXES.map(({ id }) =>
    ROLE_COLORS[roleKeyForVarna(id, profile?.levels, values[id])]
  );

  const tooltipTitle = (varnaId, count) => {
    if (!count) {
      return (
        <div className="_fv _fgap02">
          <span className="_o06">{theme(varnaId)}</span>
          <span className="_o06">{$ui('jyotish_varna_no_activations')}</span>
        </div>
      );
    }
    const roles = rolesForVarna(varnaId, profile?.levels, $ui);
    const lines = (profile?.indicators || []).filter((one) => one.varna === varnaId);
    return (
      <div className="_fv _fgap02">
        {roles.map((role) => <b key={role}>{role}</b>)}
        <span className="_o06">{theme(varnaId)} · {count}</span>
        {lines.map((one) => (
          <span key={`${one.source}_${one.chart}`}>
            {$ui(INDICATOR_LABEL_KEYS[`${one.source}_${one.chart}`])}
            {one.sign ? <> · {$ui(one.sign)}</> : null}
          </span>
        ))}
      </div>
    );
  };

  return (
    <DiamondRadarChart
      axes={AXES}
      values={values}
      colors={colors}
      ariaLabel={AXES.map(({ id }) => `${theme(id)}: ${values[id]}`).join(', ')}
      labels={AXES.map(({ id }) => ({ id, content: emoji(id) }))}
      renderLabel={({ id, count, content }) => (
        <TooltipH title={tooltipTitle(id, count)}>
          <span className="diamond-radar-label-emoji">{content}</span>
        </TooltipH>
      )}
    />
  );
}
